import { useEffect, useRef, useCallback } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { isPlatform } from '../utilities/platform';
import { startSession, endSession } from '../stores/Stats';

interface SessionData {
    sessionId: string;
    startTime: number;
    isActive: boolean;
}

const SESSION_STORAGE_KEY = 'app_session';

export const useAppSession = (userToken?: string) => {
    const sessionRef = useRef<SessionData | null>(null);
    const isInitializedRef = useRef(false);

    // Retrieve session from localStorage
    const getStoredSession = useCallback((): SessionData | null => {
        try {
            const stored = localStorage.getItem(SESSION_STORAGE_KEY);
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    }, []);

    // Save session to localStorage
    const saveSession = useCallback((session: SessionData | null) => {
        try {
            if (session) {
                localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
            } else {
                localStorage.removeItem(SESSION_STORAGE_KEY);
            }
        } catch {
            // Ignore localStorage errors
        }
    }, []);

    // Start a new session
    const startNewSession = useCallback(async () => {
        if (!userToken) return;

        try {
            const response = await startSession(userToken);
            const sessionData: SessionData = {
                sessionId: response.sessionId,
                startTime: Date.now(),
                isActive: true
            };

            sessionRef.current = sessionData;
            saveSession(sessionData);
        } catch (error) {
            console.error('Failed to start session:', error);
        }
    }, [userToken, saveSession]);

    // End the current session
    const endCurrentSession = useCallback(async (useKeepalive = false) => {
        if (!sessionRef.current || !userToken) return;

        try {
            const duration = Math.floor((Date.now() - sessionRef.current.startTime) / 1000);
            await endSession(userToken, sessionRef.current.sessionId, duration, useKeepalive);
        } catch (error) {
            console.error('Failed to end session:', error);
        } finally {
            sessionRef.current = null;
            saveSession(null);
        }
    }, [userToken, saveSession]);

    // Handle Capacitor events (mobile)
    const setupCapacitorListeners = useCallback(() => {
        if (!isPlatform('hybrid')) return;

        // App goes to background
        CapacitorApp.addListener('pause', async () => await endCurrentSession());

        // App comes back to foreground
        CapacitorApp.addListener('resume', async () => await startNewSession());
    }, [endCurrentSession, startNewSession]);

    // Handle browser events (web)
    const setupWebListeners = useCallback(() => {
        if (isPlatform('hybrid')) return;

        const handleVisibilityChange = async () => {
            if (document.hidden) {
                // Page becomes invisible (tab change, minimize)
                await endCurrentSession();
            } else {
                // Page becomes visible again
                await startNewSession();
            }
        };

        const handleBeforeUnload = () => endCurrentSession(true);

        const handleBeforeLogout = () => endCurrentSession(true);

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('beforeLogout', handleBeforeLogout);

        // Cleanup function
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('beforeLogout', handleBeforeLogout);
        };
    }, [endCurrentSession, startNewSession]);

    // Initialization
    useEffect(() => {
        if (isInitializedRef.current || !userToken) return;

        const initSession = async () => {
            // Restore existing session if it exists
            const storedSession = getStoredSession();
            if (storedSession && storedSession.isActive) {
                // If a session was active, end it first
                try {
                    const duration = Math.floor((Date.now() - storedSession.startTime) / 1000);
                    await endSession(userToken, storedSession.sessionId, duration);
                } catch (error) {
                    console.error('Failed to end previous session:', error);
                }
            }

            // Start a new session
            await startNewSession();

            // Setup listeners based on platform
            setupCapacitorListeners();
            const cleanupWeb = setupWebListeners();

            isInitializedRef.current = true;

            // Cleanup function
            return () => {
                if (cleanupWeb) cleanupWeb();
                CapacitorApp.removeAllListeners();
            };
        };

        initSession();
    }, [userToken, getStoredSession, startNewSession, setupCapacitorListeners, setupWebListeners]);

    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            if (sessionRef.current) {
                endCurrentSession();
            }
        };
    }, [endCurrentSession]);

    return {
        currentSession: sessionRef.current,
        startSession: startNewSession,
        endSession: endCurrentSession
    };
};